// api.ts
import axios, {
    AxiosError,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from "axios";
import { BASE_OWNER_URL } from "@shared/config/base-url";
import { LocalStorageKey } from "@shared/config/local-storage-key";
import { LOGIN_OK } from "@shared/config/constants";
import { clearSession } from "@shared/utils/clear-session";
// (필요시) 리이슈 응답 타입
import type { CommonDto } from "@shared/types/common-dto";
import type { SignInType } from "@shared/types/auth-token";

// ===== 1) 인스턴스 분리 =====
export const api = axios.create({
    baseURL: BASE_OWNER_URL,
    timeout: 30000,
});

// 리이슈 전용(인터셉터 없음!)
const authApi = axios.create({
    baseURL: BASE_OWNER_URL,
    timeout: 15000,
});

// ===== 2) 전역 상태 =====
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

// 토큰을 기다리는 요청 대기열 (성공/실패 콜백)
let subscribers: Array<(token: string) => void> = [];
let errorSubscribers: Array<(err: unknown) => void> = [];

const subscribeToken = (cb: (token: string) => void) => subscribers.push(cb);
const notifyAllWithToken = (token: string) => {
    subscribers.forEach((cb) => cb(token));
    subscribers = [];
};
const subscribeError = (cb: (err: unknown) => void) =>
    errorSubscribers.push(cb);
const notifyAllWithError = (err: unknown) => {
    errorSubscribers.forEach((cb) => cb(err));
    errorSubscribers = [];
};

const REISSUE_PATH = "/api/v3/accounts/reissue-tokens";
const isReissueUrl = (url?: string) => !!url && url.includes(REISSUE_PATH);

/**
 * 재발급 실패가 "복구 가능한 일시적 장애"인지 판별한다.
 * - 네트워크 단절/타임아웃(응답 없음) 또는 5xx 면 일시적 → 세션 유지(강제 로그아웃 금지).
 * - 401/403 등 인증 거부, success:false·페이로드 무효(우리가 직접 throw)는 일시적이 아님 → 로그아웃.
 * (게이트의 reissueApi 와 동일한 철학: 일시적 오류로 사용자를 강제 로그아웃시키지 않는다)
 */
const isTransientError = (err: unknown): boolean => {
    const e = err as AxiosError;
    const status = e?.response?.status;
    if (status === undefined) {
        // 응답 자체가 없음 = 네트워크 단절/타임아웃 (직접 throw 한 Error 는 code 가 없어 제외됨)
        return e?.code === "ERR_NETWORK" || e?.code === "ECONNABORTED";
    }
    return status >= 500;
};

/**
 * 진행 중인 토큰 재발급이 있으면 끝날 때까지 대기한다.
 * 인터셉터를 거치지 않고 직접 연결하는 자원(예: WebSocket)이
 * 재발급 도중의 스테일 액세스 토큰으로 붙는 것을 방지한다.
 * (재발급 성공/실패 처리는 인터셉터가 담당 — 여기서는 진행만 지연시킨다.)
 */
export const waitForTokenRefresh = async (): Promise<void> => {
    if (isRefreshing && refreshPromise) {
        try {
            await refreshPromise;
        } catch {
            // 재발급 실패는 인터셉터가 별도 처리(로그아웃/세션유지) — 여기선 차단하지 않는다.
        }
    }
};

// ===== 3) 요청 인터셉터 (게이팅 + 토큰 첨부) =====
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    // 리이슈 진행 중이면, 일반 요청은 대기 -> 새 토큰 받는 즉시 진행
    if (isRefreshing && !isReissueUrl(config.url)) {
        return new Promise((resolve, reject) => {
            subscribeToken((newToken) => {
                config.headers = config.headers ?? {};
                config.headers.Authorization = `Bearer ${newToken}`;
                resolve(config);
            });
            subscribeError(reject);
        });
    }

    // 평소에는 액세스 토큰 첨부
    const token = localStorage.getItem(LocalStorageKey.AUTHORIZATION);
    if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// ===== 4) 응답 인터셉터 (401 단일 비행 리이슈) =====
api.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
            _retry?: boolean;
        };

        const status = error.response?.status;

        // 401이고, 리이슈 요청 자체가 아니며, 재시도 플래그가 없으면
        if (status === 401 && !isReissueUrl(originalRequest?.url) && !originalRequest?._retry) {
            originalRequest._retry = true;

            // 이미 리이슈 중이면 그 결과를 기다렸다가 재요청
            if (isRefreshing && refreshPromise) {
                try {
                    const newToken = await refreshPromise;
                    originalRequest.headers = originalRequest.headers ?? {};
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return api(originalRequest);
                } catch (e) {
                    return Promise.reject(e);
                }
            }

            // 여기서부터는 내가 리이슈를 시작하는 "리더"
            isRefreshing = true;
            const refreshToken = localStorage.getItem(LocalStorageKey.RE_AUTHORIZATION);

            if (!refreshToken) {
                // 리프레시가 없으면 바로 로그아웃
                notifyAllWithError(new Error("No refresh token"));
                isRefreshing = false;
                clearSession();
                window.location.href = "/";
                return Promise.reject(error);
            }

            // 단일 비행: 한 번만 보냄
            refreshPromise = authApi
                .patch<CommonDto<SignInType>>(REISSUE_PATH, undefined, {
                    headers: { Authorization: `Bearer ${refreshToken}` },
                })
                .then((res) => {
                    const newAccessToken = res.data?.data?.accessToken;
                    const newRefreshToken = res.data?.data?.refreshToken;

                    if (!newAccessToken || !newRefreshToken) {
                        throw new Error("Invalid reissue payload");
                    }

                    //  성공 시 저장 (여기서 비로소 갱신)
                    localStorage.setItem(LocalStorageKey.AUTHORIZATION, newAccessToken);
                    localStorage.setItem(LocalStorageKey.RE_AUTHORIZATION, newRefreshToken);
                    localStorage.setItem(LocalStorageKey.LOGIN_STATE, LOGIN_OK);

                    // 대기 중인 요청들 깨우기
                    notifyAllWithToken(newAccessToken);
                    return newAccessToken;
                })
                .catch((reissueErr) => {
                    //  실패 시 대기 중인 요청들에 실패 전파
                    notifyAllWithError(reissueErr);

                    // 일시적 장애(네트워크/타임아웃/5xx)면 세션을 유지하고 요청만 reject.
                    // 인증이 실제로 무효인 경우(401/403, success:false 등)에만 강제 로그아웃.
                    if (!isTransientError(reissueErr)) {
                        clearSession();
                        window.location.href = "/";
                    }
                    throw reissueErr;
                })
                .finally(() => {
                    isRefreshing = false;
                    refreshPromise = null;
                });

            // 리더 자신도 결과를 기다렸다가 재요청
            try {
                const newToken = await refreshPromise;
                originalRequest.headers = originalRequest.headers ?? {};
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return api(originalRequest);
            } catch (e) {
                return Promise.reject(e);
            }
        }

        // 다른 에러는 통과
        return Promise.reject(error);
    }
);
